import { useEffect } from "react";

const edge = { borderColor: "rgba(255,255,255,0.12)" };

const PAGES = {
  forecast: {
    title: "Demand forecast",
    name: "Demand forecast",
    headline: ["Your next order,", "forecast-led", "and always clear."],
    lede: "See what demand might do next. Decide what to order today.",
    notes: [
      {
        title: "One forecast for the product",
        body: "Weekly demand, a likely range, and the festival weeks that move it.",
      },
      {
        title: "Change the scenario",
        body: "Service level, a late supplier, a promotion, or the stock you already hold. The order updates with them.",
      },
      {
        title: "See what that order would have done",
        body: "Replay a past week and compare fill rate against the stock you would have held.",
      },
    ],
    sectionEyebrow: "How an order is made",
    sectionTitle: "From the forecast to a quantity",
    sectionBody: "The catalogue is synthetic, and the math runs in the browser.",
    steps: [
      {
        label: "Demand",
        title: "Across the lead time",
        body: "The forecast for the weeks until a supplier delivery can arrive.",
      },
      {
        label: "Stock",
        title: "What you already hold",
        body: "Units on hand, plus anything already on order, come off that demand.",
      },
      {
        label: "Order",
        title: "Rounded to full cases",
        body: "What is left becomes the quantity, lifted to the case pack and the supplier minimum.",
      },
    ],
    closeTitle: "The planner is open",
    closeBody: "A product, a service level, and the order that follows.",
    toolUrl: "https://demo-forecast.devbroz.com",
  },
  support: {
    title: "SupportOS",
    name: "SupportOS",
    headline: ["Your customer cases,", "AI-prepared", "and always reviewed."],
    lede: "See every open case, the draft a reviewer can send, and which conversation is about to miss its time.",
    notes: [
      {
        title: "One inbox for the team",
        body: "Customers, drafts, and the people who own them live in the same queue.",
      },
      {
        title: "Never miss a reply",
        body: "Pending cases, SLA risk, and escalations sit where the reviewer already works.",
      },
      {
        title: "A person still sends it",
        body: "Agents classify the mail and draft the reply. A reviewer approves it before it goes out.",
      },
    ],
    sectionEyebrow: "A case, in order",
    sectionTitle: "From the inbox to a reply someone sends",
    steps: [
      {
        label: "01",
        title: "The mail arrives",
        body: "The channel, the customer, and the time left on the reply.",
      },
      {
        label: "02",
        title: "Agents prepare it",
        body: "Intent, policy, and a draft the reviewer can edit.",
      },
      {
        label: "03",
        title: "A person sends it",
        body: "Payment disputes and identity changes wait here.",
      },
    ],
    joinedSteps: true,
    closeTitle: "The inbox is open",
    closeBody: "The queue, the drafts, and the cases that still need a person.",
    toolUrl: "https://demo-support.devbroz.com",
  },
};

export default function DemoLanding({ id }) {
  const page = PAGES[id];

  useEffect(() => {
    const previousTitle = document.title;
    document.title = page.title;
    window.scrollTo(0, 0);
    return () => {
      document.title = previousTitle;
    };
  }, [page.title]);

  return (
    <div
      className="min-h-dvh bg-[#0a0a0a] text-white"
      style={{
        backgroundImage:
          "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(255,20,147,0.22), transparent 55%), radial-gradient(ellipse 50% 40% at 100% 0%, rgba(75,0,130,0.45), transparent 50%)",
      }}
    >
      <header className="mx-auto flex h-16 w-full max-w-5xl items-center px-5 md:px-8">
        <p className="text-sm font-semibold tracking-tight">{page.name}</p>
      </header>

      <main className="mx-auto w-full max-w-5xl px-5 pb-20 md:px-8">
        <p className="text-xs font-medium uppercase tracking-[0.22em] text-[#ff69b4]">{page.name}</p>
        <h1 className="mt-4 max-w-3xl text-4xl font-semibold leading-[1.05] tracking-tight md:text-6xl">
          {page.headline[0]}{" "}
          <span className="bg-gradient-to-r from-[#ff1493] via-[#8A2BE2] to-[#4b0082] bg-clip-text text-transparent">
            {page.headline[1]}
          </span>{" "}
          {page.headline[2]}
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#a1a1aa] md:text-lg">{page.lede}</p>

        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {page.notes.map((note) => (
            <section key={note.title} className="rounded-2xl border bg-[#121212] px-5 py-5" style={edge}>
              <h2 className="text-base font-medium">{note.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-[#a1a1aa]">{note.body}</p>
            </section>
          ))}
        </div>

        <section className="mt-16">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-[#ff69b4]">{page.sectionEyebrow}</p>
          <h2 className="mt-3 max-w-xl text-3xl font-semibold tracking-tight">{page.sectionTitle}</h2>
          {page.sectionBody ? (
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#a1a1aa]">{page.sectionBody}</p>
          ) : null}
          {page.joinedSteps ? (
            <ol className="mt-8 grid gap-px overflow-hidden rounded-3xl border bg-white/10 md:grid-cols-3" style={edge}>
              {page.steps.map((item) => (
                <li key={item.label} className="bg-[#121212] px-6 py-8">
                  <p className="text-xs font-medium tracking-[0.18em] text-[#ff6b35]">{item.label}</p>
                  <h3 className="mt-4 text-xl font-medium">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#a1a1aa]">{item.body}</p>
                </li>
              ))}
            </ol>
          ) : (
            <ol className="mt-8 grid gap-4 md:grid-cols-3">
              {page.steps.map((item) => (
                <li key={item.label} className="rounded-3xl border bg-[#121212] px-6 py-8" style={edge}>
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-[#ff6b35]">{item.label}</p>
                  <h3 className="mt-4 text-xl font-medium">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#a1a1aa]">{item.body}</p>
                </li>
              ))}
            </ol>
          )}
        </section>

        <div className="mt-16 flex flex-col items-start justify-between gap-6 border-t border-white/10 pt-10 sm:flex-row sm:items-center">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">{page.closeTitle}</h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-[#a1a1aa]">{page.closeBody}</p>
          </div>
          <a
            href={page.toolUrl}
            className="inline-flex h-14 shrink-0 items-center rounded-full bg-[#ff1493] px-10 text-base font-semibold text-white shadow-[0_0_48px_rgba(255,20,147,0.45)] hover:bg-[#ff69b4]"
          >
            Open the tool
          </a>
        </div>
      </main>
    </div>
  );
}
